/**
 * ⭐⭐ WHAT THE ASSISTANT IS ALLOWED TO SEE — the user's own permission list.
 *
 * Step 10's film says *"You decide what it can see, and you can change that at
 * any time."* This is that. It exists for two reasons, and the second is the one
 * that matters longer:
 *
 *  1. **Consent.** The assistant is fed the user's Purpose, Vision, objectives
 *     and talents whether or not they ever asked for that.
 *  2. ⭐⭐ **It is the permission layer that lets the assistant be given MORE.**
 *     Journal entries, Life Map readings and assessment results are the material
 *     a "thinking partner" would actually need to notice patterns across, and
 *     none of them reach it today. Adding them is only defensible once the user
 *     can refuse them — so this ships first, and the new sources plug in here.
 *
 * ⚠️⚠️ **TWO PLACES ENFORCE THIS, AND ONLY ONE OF THEM IS THE CLIENT.**
 *  · The CONTEXT block is assembled in the browser (`formatAIContext`), so a
 *    switched-off source is simply never sent — the data does not leave the
 *    device. That half needs no server.
 *  · The READ TOOLS run inside `ai-proxy`, server-side, under the user's JWT.
 *    Trimming the context cannot stop the model calling `list_quests`. The
 *    allowed set is therefore sent with the request and the proxy filters its
 *    toolbox — ⚠️ **which means the proxy must be DEPLOYED for those switches to
 *    do anything.** A switch that governs half of what it claims is worse than
 *    no switch.
 *
 * ⚠️⚠️ **ABSENT MEANS ON.** A key missing from the stored record is *never
 * configured*, not *off* — so turning something off writes `false` explicitly and
 * turning it back on deletes the key. Anything else makes a source added later
 * default to whatever the reader happens to assume.
 */

export type AiVisibilityKey =
  | 'identity'
  | 'direction'
  | 'talents'
  | 'quests'
  | 'openItems'
  | 'recentActivity';

export interface AiVisibilitySource {
  key: AiVisibilityKey;
  label: string;
  /** What the assistant gets, in the user's terms — never in field names. */
  blurb: string;
  /**
   * The server-side read tools this switch governs, if any.
   *
   * ⚠️ ONE switch covers the summary AND the matching tool, because "can it see
   * my quests?" is one question to a user. Splitting them would produce nine
   * switches that each need a paragraph.
   */
  tools: readonly string[];
  /** `false` for sources that exist only as a read tool (nothing in the block). */
  inContext: boolean;
}

export const AI_VISIBILITY_SOURCES: readonly AiVisibilitySource[] = [
  {
    key: 'identity',
    label: 'Your levels and domains',
    blurb: 'Your overall level, and where each of the seven domains stands.',
    tools: ['domain_status'],
    inContext: true,
  },
  {
    key: 'direction',
    label: 'Your direction',
    blurb: 'Your Purpose, your Vision, and the objective you wrote for each domain.',
    tools: [],
    inContext: true,
  },
  {
    key: 'talents',
    label: 'Your talents',
    blurb: 'Your strongest talents from the Talent assessment.',
    tools: [],
    inContext: true,
  },
  {
    key: 'quests',
    // ⚠️ Tool-only: nothing about quests rides in the context block, so this
    // switch does nothing at all until `ai-proxy` is deployed with the filter.
    label: 'Your quests',
    blurb: 'Your quests and their goals, looked up when a question needs them.',
    tools: ['list_quests'],
    inContext: false,
  },
  {
    key: 'openItems',
    label: 'What you have open',
    blurb: 'How many tasks, habits and projects you are carrying, and which ones.',
    tools: ['open_items'],
    inContext: true,
  },
  {
    key: 'recentActivity',
    label: 'Recent activity',
    blurb: 'What you have finished lately.',
    tools: [],
    inContext: true,
  },
];

/** Stored form: ONLY the explicit refusals. An absent key is allowed. */
export type AiVisibility = Partial<Record<AiVisibilityKey, boolean>>;

/**
 * ⚠️⚠️ The one rule everything else reads through. `undefined` is ALLOWED, so a
 * source added after a user last touched this screen is visible by default and
 * the user is never silently degraded by a release.
 */
export function isSourceVisible(v: AiVisibility | undefined, key: AiVisibilityKey): boolean {
  return v?.[key] !== false;
}

/**
 * The read tools this user permits, for `ai-proxy` to intersect its toolbox with.
 *
 * ⚠️⚠️ Returns EVERY governed tool when nothing is refused — and the proxy treats
 * an ABSENT field as "no restriction" so an older client bundle keeps working.
 * An explicit empty array therefore means "the user turned everything off", which
 * is a different statement from "this client does not know about visibility".
 */
export function allowedReadTools(v: AiVisibility | undefined): string[] {
  return AI_VISIBILITY_SOURCES
    .filter(s => isSourceVisible(v, s.key))
    .flatMap(s => [...s.tools]);
}

/** Every tool this feature governs. A tool absent from here is never filtered. */
export const GOVERNED_READ_TOOLS: readonly string[] =
  AI_VISIBILITY_SOURCES.flatMap(s => [...s.tools]);

/** How many sources the user has switched off — for the Settings row's summary. */
export function hiddenCount(v: AiVisibility | undefined): number {
  return AI_VISIBILITY_SOURCES.filter(s => !isSourceVisible(v, s.key)).length;
}
